// @flow

import type {MarkdocTagRegistration} from 'docs/markdoc';
import {Tag} from 'docs/util';
import {
  getPositioning,
  buildEntityDiagram,
  buildOverviewDiagram,
  buildSequenceDiagram,
  buildStateDiagram,
} from './util';

const formatError = ({id, message, type}) => ({
  id,
  level: 'critical',
  message: `[Error in ${type} diagram]: ${message}`,
});

const hasSingleParagraph = (child): boolean => {
  return (
    child.children.length === 0 ||
    (child.children.length === 1 && child.children[0].type === 'paragraph')
  );
};

const getIconChild = (node) => {
  return node.children.find((c) => c.type === 'tag:icon');
};

export const Diagram: MarkdocTagRegistration = {
  tag: 'diagram',
  component: 'Diagram',
  attributes: {
    type: {
      type: String,
      default: 'sequence',
      matches: ['sequence', 'state', 'overview', 'entity'],
    },
    description: {
      type: String,
      required: true,
      localize: true,
      description:
        'A brief, one to two sentence overview about the content in this diagram. Used as a caption for assistive devices and as backup text to show if the diagram does not render.',
    },
  },
  validate(node, config) {
    const errors = [];
    const type = node.attributes.type;
    const elements = node.children;
    // Basic validations to check that diagrams only have node and edges as direct children
    if (!elements || !elements.length) {
      errors.push(
        formatError({
          type,
          id: 'missing-children',
          message: `Diagram does not have any children.`,
        }),
      );
    }

    if (elements.find((e) => e.type !== 'tag:node' && e.type !== 'tag:edge')) {
      errors.push(
        formatError({
          type,
          id: 'invalid-children',
          message: `Found a child that is neither a node nor an edge. Diagrams can only accept {% node %} and {% edge %} tags as children.`,
        }),
      );
    }

    if (elements.find((e) => e.type !== 'tag:node' && e.type !== 'tag:edge')) {
      errors.push(
        formatError({
          type,
          id: 'invalid-children',
          message: `Found a child that is neither a node nor an edge. Diagrams can only accept {% node %} and {% edge %} tags as children`,
        }),
      );
    }

    // Validation 1: Check all the nodes for duplicate IDs
    const nodes = elements.filter((e) => e.type === 'tag:node');
    const ids = [];
    const dupes = [];

    nodes.forEach((e) => {
      if (ids.includes(e.attributes.id)) {
        dupes.push(e.attributes.id);
      } else {
        ids.push(e.attributes.id);
      }
    });

    if (dupes.length) {
      errors.push(
        formatError({
          type,
          id: 'duplicate-node-id',
          message: `Found multiple nodes using the same id. Each node needs a unique id so we know where to draw the edges. Multiple nodes are using the ${
            dupes.length > 1 ? 'ids' : 'id'
          }: [${dupes.join(',')}]`,
        }),
      );
    }

    // Validation 2: Check all the edges to make sure they are pointing to valid nodes and don't reference themselves
    const edges = elements.filter((e) => e.type === 'tag:edge');

    const missingNodes = [];
    const selfReferencingEdges = [];
    edges.forEach((e) => {
      if (!ids.includes(e.attributes.from)) {
        missingNodes.push(e.attributes.from);
      }
      if (!ids.includes(e.attributes.to)) {
        missingNodes.push(e.attributes.to);
      }
      if (e.attributes.to === e.attributes.from) {
        selfReferencingEdges.push(e.attributes.from);
      }
    });

    // It's valid for sequence diagrams to point to the same node
    if (selfReferencingEdges.length && type !== 'sequence') {
      errors.push(
        formatError({
          type,
          id: 'self-referencing',
          message: `Found edges that point to and from the same node: ${selfReferencingEdges.join(
            ',',
          )}`,
        }),
      );
    }

    // TODO(adreyfus): Ensure referencing ids of child nodes for entity diagrams is valid
    if (missingNodes.length && type !== 'entity') {
      errors.push(
        formatError({
          type,
          id: 'incorrect-edge',
          message: `Found ${
            missingNodes.length
              ? 'an edge with an invalid id'
              : 'several edges with invalid ids'
          } set as a to/for attribute. Invalid id list: [${missingNodes.join(
            ',',
          )}]. Either change the edge to point to a valid node, or add a node with the proper id.`,
        }),
      );
    }

    // Validation 3: Overview, state, and entity diagrams should only have one paragraph as edge content
    const incorrectEdges = [];
    if (['overview', 'state', 'entity'].includes(type)) {
      edges.forEach((e) => {
        if (!hasSingleParagraph(e)) {
          incorrectEdges.push(e.attributes.from);
        }
      });

      if (incorrectEdges.length) {
        errors.push(
          formatError({
            type,
            id: 'bad-edge',
            message: `Found edges (from ${incorrectEdges.join(
              ',',
            )}) with content that's more than a single paragraph. Each edge can only accept a paragraph.`,
          }),
        );
      }
    }

    // Currently only have a design for webhook icons in sequence node edges
    if (type === 'sequence') {
      const invalidIcons = [];
      edges.forEach((e) => {
        const icon = getIconChild(e);
        if (icon && icon.attributes.type !== 'webhook') {
          invalidIcons.push(icon.attributes.type);
        }
      });

      if (invalidIcons.length) {
        errors.push(
          formatError({
            type,
            id: 'invalid-icon',
            message: `Sequence diagrams can only render 'webhook' icons. Remove all other icons from your edges. List of invalid icons: ${invalidIcons.join(
              ',',
            )}`,
          }),
        );
      }
    }

    return errors;
  },
  render(node, config) {
    const attributes = node.renderAttributes(this.attributes, config);
    const children = node.renderChildren(config);
    const positioning =
      attributes.type === 'sequence' ? 'auto' : getPositioning(children);

    switch (attributes.type) {
      case 'sequence':
        attributes.elements = buildSequenceDiagram(attributes, children);
        break;
      case 'state':
        attributes.elements = buildStateDiagram(attributes, children);
        break;
      case 'overview':
        attributes.elements = buildOverviewDiagram(attributes, children);
        break;
      case 'entity':
        attributes.elements = buildEntityDiagram(attributes, children);
        break;
      default:
        attributes.elements = [];
    }
    return new Tag(this.tag, {
      ...attributes,
      positioning,
    });
  },
};
